import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMinutes } from './choreEffort';
import './MomentumBar.css';

/**
 * The reward surface.
 *
 * Finishing a chore normally produces nothing at all — the task simply stops existing, and
 * the only visible state is everything still left. That's a terrible feedback loop, and it
 * feeds the "I never get anything done" story even on days that went fine. This shows the
 * opposite side of the ledger: what you finished, how long the run is, and concrete evidence
 * in the form of named recent wins.
 *
 * The streak counts days where you did at least *one* thing. A demanding target would break
 * on the first bad day and take the motivation with it.
 */
function MomentumBar({ momentum, onRefresh }) {
  const { t } = useTranslation();
  const [showWins, setShowWins] = useState(false);

  if (!momentum) return null;

  const {
    doneToday = 0,
    minutesToday = 0,
    currentStreakDays = 0,
    longestStreakDays = 0,
    doneTodayHousehold = 0,
    recentWins = []
  } = momentum;

  const wins = recentWins?.$values || recentWins || [];
  const isRecord = currentStreakDays > 0 && currentStreakDays >= longestStreakDays;

  return (
    <div className="momentum-bar">
      <div className="momentum-stats">
        <div className={`momentum-stat ${doneToday > 0 ? 'momentum-stat-active' : ''}`}>
          <span className="momentum-stat-value">{doneToday}</span>
          <span className="momentum-stat-label">{t('chores.momentum.today')}</span>
        </div>

        <div className={`momentum-stat ${currentStreakDays > 0 ? 'momentum-stat-streak' : ''}`}>
          <span className="momentum-stat-value">
            {currentStreakDays > 0 ? '🔥' : '·'} {currentStreakDays}
          </span>
          <span className="momentum-stat-label">
            {t('chores.momentum.dayStreak')}
            {isRecord && currentStreakDays > 1 && ` · ${t('chores.momentum.personalBest')}`}
          </span>
        </div>

        {minutesToday > 0 && (
          <div className="momentum-stat">
            <span className="momentum-stat-value">{formatMinutes(minutesToday, t)}</span>
            <span className="momentum-stat-label">{t('chores.momentum.timeToday')}</span>
          </div>
        )}

        {doneTodayHousehold > doneToday && (
          <div className="momentum-stat">
            <span className="momentum-stat-value">{doneTodayHousehold}</span>
            <span className="momentum-stat-label">{t('chores.momentum.householdToday')}</span>
          </div>
        )}

        {wins.length > 0 && (
          <button
            className="momentum-wins-toggle"
            onClick={() => { setShowWins(!showWins); if (!showWins && onRefresh) onRefresh(); }}
            aria-expanded={showWins}
          >
            {showWins ? '▾' : '▸'} {t('chores.momentum.recentWins')}
          </button>
        )}
      </div>

      {doneToday === 0 && currentStreakDays > 0 && (
        <p className="momentum-nudge">
          {t('chores.momentum.keepStreak', { count: currentStreakDays })}
        </p>
      )}

      {showWins && wins.length > 0 && (
        <ul className="momentum-wins">
          {wins.map(win => (
            <li key={win.id} className="momentum-win">
              <span className="momentum-win-check" aria-hidden="true">✅</span>
              <span className="momentum-win-title">{win.title}</span>
              <span className="momentum-win-time">
                {new Date(win.completedAt).toLocaleString('nb-NO', {
                  timeZone: 'Europe/Oslo',
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default MomentumBar;
